// Web Echo detection (MYC-090): was this Echo made on my.echoflicks.com (web
// Create, no WhatsApp) rather than in a WhatsApp chat with Felix?
//
// Evidence, strongest first (indexer MYC-015, contract C13):
//   1. the ECHO row's monotonic marker `echo_channel: "web"` / `web_creation_id`
//      (stamped from any web event — the list views only have this);
//   2. a `web_create.*` session event or journey node (every web creation emits
//      `web_create.created`);
//   3. a session event / journey node whose payload says `channel: "web"` (the
//      reused lifecycle events: story.received, generation.requested, …).
// The contact's first-touch `acquisition_surface: "my_web"` is NOT evidence: it
// describes the person, and their later Echoes may well be WhatsApp ones.

import type {
  EchoJourneyNode,
  EchoflicksSessionEvent,
  EchoflicksVideoSession,
} from "../types";

export const WEB_CHANNEL = "web";
export const WEB_CREATE_EVENT_PREFIX = "web_create.";

type JsonRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isWebChannelValue = (value: unknown) =>
  typeof value === "string" && value.trim().toLowerCase() === WEB_CHANNEL;

/** True when a payload (or its nested `payload` / `channel_route`) says web. */
export function payloadIsWebChannel(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (isWebChannelValue(value.channel)) return true;
  if (isRecord(value.channel_route)) {
    if (isWebChannelValue(value.channel_route.channel)) return true;
  }
  return isRecord(value.payload) && payloadIsWebChannel(value.payload);
}

export type WebEchoRecordFields = Pick<
  EchoflicksVideoSession,
  "echo_channel" | "web_creation_id" | "echo_surface" | "presentation"
>;

/** Record-level marker only (what the Echo lists can see). */
export function isWebEchoRecord(record?: WebEchoRecordFields | null): boolean {
  if (!record) return false;
  if (isWebChannelValue(record.echo_channel)) return true;
  return (
    typeof record.web_creation_id === "string" &&
    record.web_creation_id.trim() !== ""
  );
}

export function isWebCreateEventType(value: unknown): boolean {
  return typeof value === "string" && value.startsWith(WEB_CREATE_EVENT_PREFIX);
}

export function hasWebEchoEvidence({
  events,
  journeyNodes,
}: {
  events?: Pick<EchoflicksSessionEvent, "event_type" | "raw_payload">[] | null;
  journeyNodes?:
    | Pick<EchoJourneyNode, "type" | "raw_payload" | "decision">[]
    | null;
}): boolean {
  for (const event of events ?? []) {
    if (isWebCreateEventType(event.event_type)) return true;
    if (payloadIsWebChannel(event.raw_payload)) return true;
  }
  for (const node of journeyNodes ?? []) {
    if (isWebCreateEventType(node.type)) return true;
    if (payloadIsWebChannel(node.raw_payload)) return true;
    if (payloadIsWebChannel(node.decision)) return true;
  }
  return false;
}

/** Record marker OR session evidence: the detail page's answer. */
export function isWebEcho({
  record,
  events,
  journeyNodes,
}: {
  record?: WebEchoRecordFields | null;
  events?: Pick<EchoflicksSessionEvent, "event_type" | "raw_payload">[] | null;
  journeyNodes?:
    | Pick<EchoJourneyNode, "type" | "raw_payload" | "decision">[]
    | null;
}): boolean {
  return (
    isWebEchoRecord(record) || hasWebEchoEvidence({ events, journeyNodes })
  );
}
